import React from 'react';

interface BoscovsConfigProps {
  config: {
    stockLevel: number;
    batchSize: number;
    requestsPerSecond: number;
    handlingTimeOmd?: number;
    storeHandlingTime?: number;
    providerSpecificHandlingTime: number;
    updateFlagValue: number;
    priceMarkupPercentage?: number;
    enableMinimumPriceCheck?: boolean;
    minimumPriceThreshold?: number;
    minimumPriceMessage?: string;
    enableMinimumStockCheck?: boolean;
    minimumStockThreshold?: number;
  };
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const BoscovsConfig: React.FC<BoscovsConfigProps> = ({ config, onChange }) => {
  return (
    <div className="space-y-4">
      <div>
        <label htmlFor="stockLevel" className="block text-sm font-medium text-gray-700 mb-1">
          Nível de Estoque (Boscovs)
        </label>
        <input
          id="stockLevel"
          type="number"
          name="stockLevel"
          value={config.stockLevel || ''}
          onChange={onChange}
          className="input"
          aria-label="Nível de estoque para Boscovs"
        />
        <p className="text-sm text-gray-500 mt-1">
          Limite máximo de estoque a ser considerado para produtos da Boscovs.
        </p>
      </div>

      <div>
        <label htmlFor="storeHandlingTime" className="block text-sm font-medium text-gray-700 mb-1">
          Tempo de Manuseio OMD (dias)
        </label>
        <input
          id="storeHandlingTime"
          type="number"
          name="storeHandlingTime"
          value={config.storeHandlingTime || config.handlingTimeOmd || ''}
          onChange={onChange}
          className="input"
          aria-label="Tempo de manuseio da OMD em dias"
        />
        <p className="text-sm text-gray-500 mt-1">
          Tempo de manuseio da OMD para produtos da Boscovs.
        </p>
      </div>

      <div>
        <label htmlFor="boscovsHandlingTime" className="block text-sm font-medium text-gray-700 mb-1">
          Tempo de Manuseio Boscovs (dias)
        </label>
        <input
          id="boscovsHandlingTime"
          type="number"
          name="providerSpecificHandlingTime"
          value={config.providerSpecificHandlingTime || ''}
          onChange={onChange}
          className="input"
          aria-label="Tempo de manuseio da Boscovs em dias"
        />
        <p className="text-sm text-gray-500 mt-1">
          Tempo de manuseio específico da Boscovs para seus produtos.
        </p>
      </div>

      <div>
        <label htmlFor="batchSize" className="block text-sm font-medium text-gray-700 mb-1">
          Tamanho do Lote
        </label>
        <input
          id="batchSize"
          type="number"
          name="batchSize"
          value={config.batchSize || ''}
          onChange={onChange}
          className="input"
          aria-label="Tamanho do lote"
        />
        <p className="text-sm text-gray-500 mt-1">
          Número de SKUs por requisição à API (atualização em batch).
        </p>
      </div>

      <div>
        <label htmlFor="requestsPerSecond" className="block text-sm font-medium text-gray-700 mb-1">
          Requisições por Segundo
        </label>
        <input
          id="requestsPerSecond"
          type="number"
          name="requestsPerSecond"
          value={config.requestsPerSecond || ''}
          onChange={onChange}
          className="input"
          aria-label="Requisições por segundo"
        />
        <p className="text-sm text-gray-500 mt-1">
          Taxa de requisições à API da Boscovs (limite para evitar bloqueio).
        </p>
      </div>

      <div>
        <label htmlFor="updateFlagValue" className="block text-sm font-medium text-gray-700 mb-1">
          Código de Atualização Boscovs
        </label>
        <input
          id="updateFlagValue"
          type="number"
          name="updateFlagValue"
          value={config.updateFlagValue || ''}
          onChange={onChange}
          className="input"
          aria-label="Código de atualização Boscovs"
        />
        <p className="text-sm text-gray-500 mt-1">
          Identificador usado para marcar produtos atualizados pela Boscovs no banco de dados.
        </p>
      </div>

      <div className="bg-blue-50 p-4 rounded-md border border-blue-200 mt-4">
        <h3 className="text-blue-800 font-medium mb-2">Informações da Boscovs</h3>
        <p className="text-sm text-blue-700">
          A Boscovs utiliza integração baseada em brand/categoria para obter disponibilidade e preços. Configure valores adequados para
          garantir prazos de entrega realistas.
        </p>
      </div>

      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-yellow-900 mb-3">
          Minimum Price Threshold
        </h3>

        <div className="mb-4">
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              name="enableMinimumPriceCheck"
              checked={config.enableMinimumPriceCheck || false}
              onChange={onChange}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm font-medium text-gray-700">
              Enable Minimum Price Check
            </span>
          </label>
          <p className="text-sm text-gray-500 mt-1 ml-6">
            When enabled, products with price below the threshold will be marked as out of stock
          </p>
        </div>

        {config.enableMinimumPriceCheck && (
          <div className="space-y-3">
            <div>
              <label htmlFor="minimumPriceThreshold" className="block text-sm font-medium text-gray-700 mb-1">
                Minimum Price Threshold ($)
              </label>
              <input
                id="minimumPriceThreshold"
                type="number"
                name="minimumPriceThreshold"
                min="0"
                step="0.01"
                value={config.minimumPriceThreshold || 0}
                onChange={onChange}
                className="input"
              />
              <p className="text-sm text-gray-500 mt-1">
                Products with price below this value will be marked as out of stock
              </p>
            </div>

            <div>
              <label htmlFor="minimumPriceMessage" className="block text-sm font-medium text-gray-700 mb-1">
                Minimum Price Message
              </label>
              <input
                id="minimumPriceMessage"
                type="text"
                name="minimumPriceMessage"
                value={config.minimumPriceMessage || ''}
                onChange={onChange}
                className="input"
              />
              <p className="text-sm text-gray-500 mt-1">
                Optional message to be used when a product is marked out of stock due to minimum price
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-yellow-900 mb-3">
          Minimum Stock Threshold
        </h3>

        <div className="mb-4">
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              name="enableMinimumStockCheck"
              checked={config.enableMinimumStockCheck || false}
              onChange={onChange}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm font-medium text-gray-700">
              Enable Minimum Stock Check
            </span>
          </label>
          <p className="text-sm text-gray-500 mt-1 ml-6">
            When enabled, products with stock below the threshold will be marked as out of stock
          </p>
        </div>

        {config.enableMinimumStockCheck && (
          <div>
            <label htmlFor="minimumStockThreshold" className="block text-sm font-medium text-gray-700 mb-1">
              Minimum Stock Threshold
            </label>
            <input
              id="minimumStockThreshold"
              type="number"
              name="minimumStockThreshold"
              min="0"
              value={config.minimumStockThreshold || 8}
              onChange={onChange}
              className="input"
            />
            <p className="text-sm text-gray-500 mt-1">
              Products with stock below or equal to this value will be marked as out of stock
            </p>
            <p className="text-sm text-yellow-700 mt-2">
              <strong>Important:</strong> This helps prevent selling products that are low in stock
            </p>
          </div>
        )}
      </div>

      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-purple-900 mb-3">
          Phase 2.1 - Price Markup
        </h3>

        <div>
          <label htmlFor="priceMarkupPercentage" className="block text-sm font-medium text-gray-700 mb-1">
            Markup Percentage (%)
          </label>
          <input
            id="priceMarkupPercentage"
            type="number"
            name="priceMarkupPercentage"
            min="0"
            step="0.1"
            value={config.priceMarkupPercentage || 15}
            onChange={onChange}
            className="input"
          />
          <p className="text-sm text-gray-500 mt-1">
            Percentual de markup aplicado ao calcular o preço Amazon.
          </p>
          <p className="text-sm text-purple-700 mt-2">
            <strong>Fórmula:</strong> Amazon Price = (supplier_price + shipping) × (1 + markup% / 100)
          </p>
          <p className="text-sm text-purple-700 mt-1">
            <strong>Exemplo:</strong> Produto $10 + Shipping $5 com 15% markup = $17.25
          </p>
        </div>
      </div>
    </div>
  );
};

export default BoscovsConfig;
